import apaleoApi from 'api/apaleoApi';
import {
  TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  TOKEN_EXPIRY_KEY,
} from 'utils/apaleoAuth';

interface PropertyWithRatePlans {
  code: string;
  ratePlans: Array<{
    id: string;
    code: string;
    name: string;
    description: string;
  }>;
  numberOfRooms?: number | null;
  starRating?: number | null;
  chainName?: string;
}

interface PropertySelectionRequest {
  integrationRequestId: number | null;
  email: string;
  selectedProperties: PropertyWithRatePlans[];
}

interface PropertySelectionResponse {
  success: boolean;
  message?: string;
}

export interface RatePlanDto {
  id: string;
  code: string;
  name: string;
  description: string;
}


export interface Property {
  id: string;
  name: string;
  location: {
    city?: string;
    countryCode?: string;
    formattedLocation?: string;
    [key: string]: any;
  };
  ratePlans?: Array<{
    id: string;
    name: string;
    code: string;
    description: string;
  }>;
  [key: string]: any;
}

export interface RatePlanResponseDto {
  success: boolean;
  message: string;
  ratePlans: RatePlanDto[];
  count: number;
}

const apaleoApiSlice = apaleoApi.injectEndpoints({
  endpoints: (builder) => ({
    getProperties: builder.query<any, void>({
      query: () => {
        const token = localStorage.getItem(TOKEN_KEY) ?? '';
        return {
          url: `/api/apaleo/properties?token=${encodeURIComponent(token)}`,
          method: 'GET',
        };
      },
      providesTags: ['Properties'],
    }),
    exchangeCodeForToken: builder.mutation({
      query: ({ code, state }: { code: string; state: string }) => ({
        url: '/api/apaleo/token-exchange',
        method: 'POST',
        data: { code, state },
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data?.accessToken) {
            localStorage.setItem(TOKEN_KEY, data.accessToken);

            if (data.refresh_token) {
              localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
            }

            if (data.expiresIn) {
              localStorage.setItem(
                TOKEN_EXPIRY_KEY,
                (Date.now() + data.expiresIn * 1000).toString()
              );
            }
          }
        } catch (error) {
          console.error('Token exchange failed:', error);
          // Don't re-throw - let the component handle the error
        }
      },
      invalidatesTags: ['Token'],
    }),
    submitSelectedProperties: builder.mutation<
      PropertySelectionResponse,
      PropertySelectionRequest
    >({
      query: (data) => {
        const token = localStorage.getItem(TOKEN_KEY) ?? '';
        return {
          url: `/api/apaleo/properties?token=${encodeURIComponent(token)}`,
          method: 'POST',
          headers: {},
          data: {
            integrationRequestId: data?.integrationRequestId,
            email: data.email,
            selectedProperties: data.selectedProperties,
          },
        };
      },
      invalidatesTags: ['IntegrationRequest'],
    }),
    getRatePlans: builder.query<RatePlanResponseDto, { propertyId: string }>({
      query: ({ propertyId }) => {
        const token = localStorage.getItem(TOKEN_KEY) ?? '';
        return {
          url: `/api/apaleo/rateplan?propertyId=${encodeURIComponent(propertyId)}&token=${encodeURIComponent(token)}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { propertyId }) =>
        result?.success ? [{ type: 'RatePlan', id: propertyId }] : [],
    }),
  }),
});

export const {
  useGetPropertiesQuery,
  useExchangeCodeForTokenMutation,
  useSubmitSelectedPropertiesMutation,
  useGetRatePlansQuery,
} = apaleoApiSlice;
